import { Button, Heading, Input, LinkText } from '@postscript/components';
import { FormEvent, useState } from 'react';
import styled from 'styled-components';
import NavLogo from '../img/NavLogo';

interface PasswordGateProps {
  onAuthenticate: (password: string) => boolean;
}

const StyledAuthPageWrapper = styled.main`
  background: var(--surface-bkg-color);
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-4);
  padding-bottom: 10vh;
`;

const StyledLoginWrapper = styled.div`
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  text-align: center;
`;

const StyledLogoLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--spacing-4);
  outline: none !important;
  transform-origin: center;

  path {
    transition: fill var(--hover-transition);
  }

  --logo-bkg-color: var(--purple-core);
  --logo-text-color: var(--white);

  --logo-bkg-hover-color: var(--purple-7);
  --logo-bkg-active-color: var(--purple-8);

  [data-theme='dark'] & {
    --logo-bkg-color: var(--purple-5);
    --logo-bkg-hover-color: var(--purple-6);
    --logo-bkg-active-color: var(--purple-7);
  }

  &:hover {
    --logo-bkg-color: var(--logo-bkg-hover-color);
  }

  &:active {
    --logo-bkg-color: var(--logo-bkg-active-color);
  }

  &:not(:hover):focus {
    --logo-bkg-color: var(--logo-bkg-hover-color);
  }
`;

const StyledHeading = styled(Heading)`
  margin-bottom: var(--spacing-4);
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

const AuthButton = styled(Button)`
  width: 100%;
  text-align: center;

  &:not(:hover):not(:active):not(:focus) {
    background: var(--text-color);
  }
`;

const LinksContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  margin-top: var(--spacing-2);
`;

const Separator = styled.span`
  color: var(--text-color-dim);
`;

const PasswordGate = ({ onAuthenticate }: PasswordGateProps) => {
  const [password, setPassword] = useState('');
  const [hasError, setHasError] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setHasError(false);

    const success = onAuthenticate(password);

    if (!success) {
      setHasError(true);
      setPassword('');
    }
  };

  return (
    <StyledAuthPageWrapper>
      <StyledLoginWrapper>
        <StyledLogoLink href="https://postscript.io">
          <NavLogo width="144" height="45" />
        </StyledLogoLink>
        <StyledHeading size="large" forwardedAs="h1">
          Design Sandbox
        </StyledHeading>
        <StyledForm onSubmit={handleSubmit}>
          <Input
            type="password"
            onChange={(e) => setPassword(e.target.value)}
            id="password"
            name="password"
            placeholder="Enter password"
            value={password}
            autoFocus
            error={hasError}
            errorMessage="Incorrect password. Please try again."
          />
          <AuthButton type="submit" variant="primary">
            Enter
          </AuthButton>
          <LinksContainer>
            <LinkText
              href="https://postscript.io"
              target="_blank"
              rel="noopener noreferrer"
            >
              Postscript Website
            </LinkText>
            <Separator>•</Separator>
            <LinkText
              href="https://app.postscript.io"
              target="_blank"
              rel="noopener noreferrer"
            >
              Postscript App
            </LinkText>
          </LinksContainer>
        </StyledForm>
      </StyledLoginWrapper>
    </StyledAuthPageWrapper>
  );
};

export default PasswordGate;
